import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';

import { Alert, PageHeader, useToast } from '@platform/ui';

import {
  DEFAULT_FEEDBACK_FILTERS,
  FeedbackBoard,
  type FeedbackFilters,
} from '../../features/feedback/feedback-board';
import { tenantOptionsKey } from '../../features/tenants/use-tenant';
import {
  PLATFORM_ENDPOINT_PERMISSIONS,
  type ModerateFeedbackRequest,
  type PlatformFeedbackItem,
} from '../../lib/api/platform';
import { usePlatformClient } from '../../lib/api/platform-client-context';
import { RequirePermission } from '../../lib/session/guards';
import { usePermissions } from '../../lib/session/permissions';

export const Route = createFileRoute('/feedback/')({
  component: PlatformFeedbackRoute,
});

/**
 * `/feedback` (FR-SUPER-08, rules §50, `GET /platform/feedback`).
 *
 * Moderation is not a step-up surface: changing a status or hiding an item is reversible
 * and leaves the row intact, so a confirmation dialog on every click would train the
 * operator to dismiss dialogs. What it *is* is permission-gated — `feedback.moderate`,
 * which `platform_support` does not hold — and every write is an audited `PATCH`.
 */
function PlatformFeedbackRoute() {
  return (
    <RequirePermission keys={[PLATFORM_ENDPOINT_PERMISSIONS.feedbackModerate]}>
      <PlatformFeedbackPage />
    </RequirePermission>
  );
}

function PlatformFeedbackPage() {
  const client = usePlatformClient();
  const queryClient = useQueryClient();
  const { can } = usePermissions();
  const { toast } = useToast();

  const [filters, setFilters] = useState<FeedbackFilters>(DEFAULT_FEEDBACK_FILTERS);
  const [error, setError] = useState<string | undefined>(undefined);

  const items = useInfiniteQuery({
    queryKey: ['platform-feedback', filters],
    queryFn: ({ pageParam }) => client.listFeedback({ ...filters, limit: 50, cursor: pageParam }),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.page.nextCursor,
  });
  const tenants = useQuery({
    queryKey: tenantOptionsKey(),
    queryFn: async () => (await client.listTenants({ limit: 100 })).data,
  });

  const refresh = (): Promise<void> =>
    queryClient.invalidateQueries({ queryKey: ['platform-feedback'] });

  const moderate = useMutation({
    mutationFn: ({
      item,
      patch,
    }: {
      item: PlatformFeedbackItem;
      patch: ModerateFeedbackRequest;
      message: string;
    }) => client.updateFeedback(item.id, patch),
    onSuccess: async (_result, variables) => {
      setError(undefined);
      toast({ tone: 'success', message: variables.message });
      await refresh();
    },
    onError: (caught: Error) => {
      setError(caught.message);
    },
  });

  const comment = useMutation({
    mutationFn: ({
      item,
      body,
      isInternal,
    }: {
      item: PlatformFeedbackItem;
      body: string;
      isInternal: boolean;
    }) => client.commentOnFeedback(item.id, { body, isInternal }),
    onSuccess: async (_result, variables) => {
      setError(undefined);
      toast({
        tone: 'success',
        message: variables.isInternal ? 'Internal note added.' : 'Comment posted.',
      });
      await refresh();
    },
    onError: (caught: Error) => {
      setError(caught.message);
    },
  });

  if (items.isError || tenants.isError) {
    return (
      <>
        <PageHeader title="Feedback" />
        <Alert tone="error">Feedback could not be loaded. Try again in a moment.</Alert>
      </>
    );
  }

  return (
    <FeedbackBoard
      items={(items.data?.pages ?? []).flatMap((page) => page.data)}
      hasMore={items.hasNextPage}
      loadingMore={items.isFetchingNextPage}
      onLoadMore={() => {
        void items.fetchNextPage();
      }}
      tenants={(tenants.data ?? []).map((tenant) => ({ id: tenant.id, name: tenant.name }))}
      filters={filters}
      onFiltersChange={setFilters}
      pending={items.isPending || tenants.isPending}
      canModerate={can(PLATFORM_ENDPOINT_PERMISSIONS.feedbackModerate)}
      error={error}
      onStatusChange={(item, status) => {
        moderate.mutate({
          item,
          patch: { status },
          message: 'Status updated.',
        });
      }}
      onVisibilityChange={(item, visibility) => {
        moderate.mutate({
          item,
          patch: { visibility },
          message:
            visibility === 'public'
              ? 'Published to the public board.'
              : 'Hidden from the public board.',
        });
      }}
      onMerge={(item, targetId) => {
        moderate.mutate({
          item,
          patch: { duplicateOfId: targetId },
          message: targetId === null ? 'No longer a duplicate.' : 'Merged as a duplicate.',
        });
      }}
      onComment={(item, body, isInternal) => {
        comment.mutate({ item, body, isInternal });
      }}
    />
  );
}
